export type TreatmentLineFilter = 'all' | 'untreated' | 'previous_treated';

export interface TimelineAssumptions {
  /** Months after primary completion for regulatory filing (default 3–4). */
  filingMonthsAfterPrimary: number;
  /** Standard FDA review cycle after filing. */
  standardReviewMonths: number;
  /** Accelerated / priority review cycle after filing. */
  acceleratedReviewMonths: number;
}

export const DEFAULT_TIMELINE_ASSUMPTIONS: TimelineAssumptions = {
  filingMonthsAfterPrimary: 3.5,
  standardReviewMonths: 9,
  acceleratedReviewMonths: 6,
};

export const TIMELINE_ASSUMPTIONS_STORAGE_KEY = 'ruby_ongoing_trials_timeline_assumptions';

export function loadTimelineAssumptions(): TimelineAssumptions {
  try {
    const raw = localStorage.getItem(TIMELINE_ASSUMPTIONS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_TIMELINE_ASSUMPTIONS };
    const parsed = JSON.parse(raw) as Partial<TimelineAssumptions>;
    return {
      filingMonthsAfterPrimary:
        typeof parsed.filingMonthsAfterPrimary === 'number'
          ? parsed.filingMonthsAfterPrimary
          : DEFAULT_TIMELINE_ASSUMPTIONS.filingMonthsAfterPrimary,
      standardReviewMonths:
        typeof parsed.standardReviewMonths === 'number'
          ? parsed.standardReviewMonths
          : DEFAULT_TIMELINE_ASSUMPTIONS.standardReviewMonths,
      acceleratedReviewMonths:
        typeof parsed.acceleratedReviewMonths === 'number'
          ? parsed.acceleratedReviewMonths
          : DEFAULT_TIMELINE_ASSUMPTIONS.acceleratedReviewMonths,
    };
  } catch {
    return { ...DEFAULT_TIMELINE_ASSUMPTIONS };
  }
}

export function saveTimelineAssumptions(assumptions: TimelineAssumptions) {
  localStorage.setItem(TIMELINE_ASSUMPTIONS_STORAGE_KEY, JSON.stringify(assumptions));
}

export interface OngoingTrialTimelineInput {
  id: number;
  trial_name?: string;
  full_title?: string;
  phases?: string;
  nct_id?: string;
  study_start_date?: string | null;
  primary_completion_date?: string | null;
  study_completion_date?: string | null;
  completion_date?: string | null;
  overall_status?: string | null;
  trial_status?: string | null;
  indication_details?: string;
  indication_details_text?: string;
  brief_summary?: string;
  line_of_therapy?: string | null;
  intervention_names?: string[];
  regimen_details?: unknown;
  enrollment_size?: string;
  company_name?: string | null;
  organization_full_name?: string;
  sponsor?: string;
  asset_id?: number | null;
  asset_ids?: number[];
  asset_name?: string | null;
  indication_code?: string;
}

export function isCombinationTrial(trial: OngoingTrialTimelineInput): boolean {
  const title = `${trial.trial_name || ''} ${trial.full_title || ''}`.toLowerCase();
  if (/\bcombination\b|\bplus\b|\bwith or without\b|\bin combination with\b/.test(title)) return true;
  const ints = trial.intervention_names || [];
  const active = ints.filter((n) => n && !/placebo|standard of care|soc/i.test(n));
  return active.length > 1;
}

export function formatRegimenSnippet(trial: OngoingTrialTimelineInput): string {
  const rd = trial.regimen_details;
  if (rd && typeof rd === 'object') {
    const exp = (rd as Record<string, unknown>).experimental ?? (rd as Record<string, unknown>).exp;
    const ctrl = (rd as Record<string, unknown>).control ?? (rd as Record<string, unknown>).ctrl;
    const parts = [exp, ctrl].filter((v) => v != null && String(v).trim()).map(String);
    if (parts.length) return parts.join(' vs ');
  }
  const ints = (trial.intervention_names || []).filter((n) => n && !/placebo/i.test(n));
  if (ints.length) return ints.slice(0, 3).join(' + ');
  return '';
}

export function phaseShortLabel(phase?: string): string {
  if (!phase) return '—';
  const p = phase.toLowerCase().replace(/\s+/g, '');
  if (p.includes('phase3') || p === '3') return 'P3';
  if (p.includes('phase2/3') || p.includes('phase2-3')) return 'P2/3';
  if (p.includes('phase2') || p === '2') return 'P2';
  if (p.includes('phase1/2') || p.includes('phase1-2')) return 'P1/2';
  if (p.includes('phase1') || p === '1') return 'P1';
  return phase.replace(/phase\s*/gi, 'P').trim();
}

export function parseTimelineDate(value?: string | null): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function addMonths(date: Date, months: number): Date {
  const d = new Date(date.getTime());
  d.setTime(d.getTime() + months * (365.25 / 12) * 24 * 60 * 60 * 1000);
  return d;
}

export interface TrialMilestones {
  start: Date | null;
  primaryCompletion: Date | null;
  studyCompletion: Date | null;
  dataReadout: Date | null;
  filing: Date | null;
  approvalUs: Date | null;
  approvalEu: Date | null;
  isCompleted: boolean;
  showProjectedMilestones: boolean;
}

export function resolveStudyCompletionDate(trial: OngoingTrialTimelineInput): Date | null {
  return (
    parseTimelineDate(trial.study_completion_date) || parseTimelineDate(trial.completion_date)
  );
}

export function isTrialCompleted(trial: OngoingTrialTimelineInput): boolean {
  const status = `${trial.overall_status || ''} ${trial.trial_status || ''}`.toLowerCase();
  if (/\bcompleted\b/.test(status) && !/not yet recruiting|not yet completed/.test(status)) {
    return true;
  }
  const studyEnd = resolveStudyCompletionDate(trial);
  if (studyEnd && studyEnd.getTime() <= Date.now()) {
    return true;
  }
  return false;
}

export function computeTrialMilestones(
  trial: OngoingTrialTimelineInput,
  assumptions: TimelineAssumptions,
  accelerated: boolean,
): TrialMilestones {
  const start = parseTimelineDate(trial.study_start_date);
  const primaryCompletion = parseTimelineDate(trial.primary_completion_date);
  const studyCompletion = resolveStudyCompletionDate(trial);
  const completed = isTrialCompleted(trial);
  const dataReadout = primaryCompletion;
  const showProjectedMilestones = !completed;
  const filing =
    showProjectedMilestones && primaryCompletion
      ? addMonths(primaryCompletion, assumptions.filingMonthsAfterPrimary)
      : null;
  const reviewMonths = accelerated ? assumptions.acceleratedReviewMonths : assumptions.standardReviewMonths;
  const approvalUs = filing ? addMonths(filing, reviewMonths) : null;
  const approvalEu = approvalUs ? addMonths(approvalUs, 1) : null;
  return {
    start,
    primaryCompletion,
    studyCompletion,
    dataReadout,
    filing,
    approvalUs,
    approvalEu,
    isCompleted: completed,
    showProjectedMilestones,
  };
}

export function inferAcceleratedApproval(trial: OngoingTrialTimelineInput): boolean {
  const hay = `${trial.trial_name || ''} ${trial.full_title || ''} ${trial.brief_summary || ''}`.toLowerCase();
  return /\baccelerated\b|\bbreakthrough\b|\bpriority review\b|\bfast track\b/.test(hay);
}

export function monthIndex(date: Date, rangeStart: Date): number {
  return (date.getFullYear() - rangeStart.getFullYear()) * 12 + (date.getMonth() - rangeStart.getMonth());
}

export function formatTimelineMonth(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}

export function formatTimelineDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export const DEFAULT_TIMELINE_VIEW_YEAR = 2026;

export function trialInstitutionLabel(trial: OngoingTrialTimelineInput): string {
  return (
    trial.company_name?.trim() ||
    trial.sponsor?.trim() ||
    trial.organization_full_name?.trim() ||
    ''
  );
}

export function trialPrimaryAssetId(trial: OngoingTrialTimelineInput): number | null {
  if (trial.asset_id != null) return trial.asset_id;
  const first = trial.asset_ids?.[0];
  return first != null ? first : null;
}

export function trialTimelineAnchorDate(
  trial: OngoingTrialTimelineInput,
  assumptions: TimelineAssumptions,
  accelerated: boolean,
): Date | null {
  const m = computeTrialMilestones(trial, assumptions, accelerated);
  const candidates = [m.start, m.primaryCompletion, m.studyCompletion, m.filing, m.approvalUs].filter(
    (d): d is Date => d != null,
  );
  if (candidates.length === 0) return null;
  return new Date(Math.min(...candidates.map((d) => d.getTime())));
}

export function compareTrialsByTimeline(
  a: OngoingTrialTimelineInput,
  b: OngoingTrialTimelineInput,
  assumptions: TimelineAssumptions,
  acceleratedFor: (id: number) => boolean,
): number {
  const aDate = trialTimelineAnchorDate(a, assumptions, acceleratedFor(a.id));
  const bDate = trialTimelineAnchorDate(b, assumptions, acceleratedFor(b.id));
  const aTime = aDate?.getTime() ?? Number.POSITIVE_INFINITY;
  const bTime = bDate?.getTime() ?? Number.POSITIVE_INFINITY;
  if (aTime !== bTime) return aTime - bTime;
  const aAsset = a.asset_name || '';
  const bAsset = b.asset_name || '';
  return aAsset.localeCompare(bAsset);
}

export function formatTimelineQuarter(date: Date): string {
  const q = Math.floor(date.getMonth() / 3) + 1;
  return `Q${q} ${date.getFullYear()}`;
}
